/**
 * 📣 COMUNIDAD — lógica PURA del feed (sin DB, sin framework), unit-testada.
 *
 * El feed cronológico, los contadores de likes/comentarios y la validación del
 * borrador de publicación son cálculos deterministas aislados de PowerSync y de
 * Solid. La query reactiva (`community/queries.ts`) trae las filas crudas; aquí
 * viven las transformaciones puras que la UI consume.
 */

/** Máximo de imágenes por publicación (el compositor limita a esto). */
export const MAX_POST_MEDIA = 5;

/** Longitud máxima del pie de foto. */
export const MAX_CAPTION_LENGTH = 2000;

/** Motivos de reporte (pantalla `reportar_publicacion`). */
export const REPORT_REASONS = ["inappropriate", "spam", "copyright", "other"] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

/**
 * Parsea el campo `media` (array JSON de URLs almacenado como texto en la fila
 * local-first). Robusto ante nulos / JSON corrupto: devuelve `[]`.
 */
export function parseMedia(media: string | null | undefined): string[] {
  if (!media) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(media);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((x): x is string => typeof x === "string" && x.length > 0);
  } catch {
    return [];
  }
}

/** Serializa una lista de URLs de media al texto JSON que guarda la fila. */
export function serializeMedia(urls: readonly string[]): string {
  const clean = urls
    .map((u) => u.trim())
    .filter((u) => u.length > 0)
    .slice(0, MAX_POST_MEDIA);
  return JSON.stringify(clean);
}

/**
 * Cuenta cuántas filas hay por `post_id` (para likes o comentarios). Determinista.
 */
export function countByPostId(rows: readonly { post_id: string | null }[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const row of rows) {
    if (row.post_id) {
      map[row.post_id] = (map[row.post_id] ?? 0) + 1;
    }
  }
  return map;
}

/**
 * Conjunto de `post_id` que el usuario dado ha likeado (para pintar el corazón
 * relleno y togglear). Filas de `post_like` ya sincronizadas.
 */
export function likedPostIds(
  likes: readonly { post_id: string | null; user_id: string | null }[],
  userId: string,
): Set<string> {
  const set = new Set<string>();
  for (const like of likes) {
    if (like.user_id === userId && like.post_id) {
      set.add(like.post_id);
    }
  }
  return set;
}

/**
 * Ordena publicaciones por recencia (created_at desc). Usa `toSorted` (no muta la
 * entrada; receta GAMIF-3). `created_at` ausente cae al final.
 */
export function sortPostsByRecent<T extends { created_at: string | null }>(
  posts: readonly T[],
): T[] {
  return posts.toSorted((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
}

export type RelativeTimeUnit = "now" | "minute" | "hour" | "day" | "week";
export interface RelativeTime {
  unit: RelativeTimeUnit;
  value: number;
}

const SECONDS = 1000;
const MINUTE = 60 * SECONDS;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Tiempo relativo PURO e i18n-agnóstico: devuelve la unidad + magnitud, y la UI
 * la localiza (`hace 2 horas`). Robusto ante fechas inválidas → `{now,0}`.
 */
export function relativeTime(iso: string | null | undefined, nowMs: number): RelativeTime {
  if (!iso) {
    return { unit: "now", value: 0 };
  }
  const then = Date.parse(iso);
  if (Number.isNaN(then)) {
    return { unit: "now", value: 0 };
  }
  const diff = Math.max(0, nowMs - then);
  if (diff >= WEEK) {
    return { unit: "week", value: Math.floor(diff / WEEK) };
  }
  if (diff >= DAY) {
    return { unit: "day", value: Math.floor(diff / DAY) };
  }
  if (diff >= HOUR) {
    return { unit: "hour", value: Math.floor(diff / HOUR) };
  }
  if (diff >= MINUTE) {
    return { unit: "minute", value: Math.floor(diff / MINUTE) };
  }
  return { unit: "now", value: 0 };
}

export interface PostDraft {
  caption: string;
  media: readonly string[];
  productId: string | null;
  rating: number | null;
}

export interface DraftValidation {
  ok: boolean;
  errors: string[];
}

/**
 * Valida un borrador de publicación (pantalla `crear_nueva_publicacion`):
 *   - debe llevar pie de foto O al menos una imagen,
 *   - como mucho `MAX_POST_MEDIA` imágenes,
 *   - pie ≤ `MAX_CAPTION_LENGTH`,
 *   - rating, si existe, en 1..5.
 * Los códigos de error son claves estables (la UI las localiza).
 */
export function validatePostDraft(draft: PostDraft): DraftValidation {
  const errors: string[] = [];
  const caption = draft.caption.trim();
  const media = draft.media.filter((u) => u.trim().length > 0);
  if (caption.length === 0 && media.length === 0) {
    errors.push("empty");
  }
  if (caption.length > MAX_CAPTION_LENGTH) {
    errors.push("caption_too_long");
  }
  if (media.length > MAX_POST_MEDIA) {
    errors.push("too_many_media");
  }
  if (draft.rating !== null && (draft.rating < 1 || draft.rating > 5)) {
    errors.push("rating_out_of_range");
  }
  return { ok: errors.length === 0, errors };
}

/** ¿Es `reason` un motivo de reporte válido? (guard del formulario/servidor). */
export function isValidReportReason(reason: string): reason is ReportReason {
  return (REPORT_REASONS as readonly string[]).includes(reason);
}
